import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  InputBase,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import {
  UserFeedback,
  QuestionFeedback,
  QuestionTurnFeedback,
  GroupedFeedbackComment,
  GroupedFeedbackSession,
} from "../../api/derin/interface";
import {
  derinColors,
  derinTypography,
  derinSpacing,
  derinCardStyles,
  derinInputStyles,
  getColorForUser,
} from "./theme";
import SectionHeader from "./SectionHeader";
import VerticalBar from "./VerticalBar";

const formatFeedbackDate = (isoDate: string): string => {
  const date = new Date(isoDate);
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const formatDate = (isoDate: string): string => {
  const date = new Date(isoDate);
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }) + " at " + date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

interface FeedbackDetailViewProps {
  group: GroupedFeedbackSession;
  onBack: () => void;
}

const FeedbackDetailView: React.FC<FeedbackDetailViewProps> = ({
  group,
  onBack,
}) => {
  const [status, setStatus] = useState<"New" | "Reviewed">(
    group.status as "New" | "Reviewed"
  );
  const [adminFilter, setAdminFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Transform grouped data into display format ok
  const { surveyLevelFeedback, questionFeedback, admins } = useMemo(() => {
    const surveyLevel: UserFeedback[] = [];
    const questions: QuestionFeedback[] = [];

    const transformComment = (c: GroupedFeedbackComment): UserFeedback => ({
      id: String(c.id),
      userName: c.userName,
      timestamp: formatFeedbackDate(c.timestamp),
      comment: c.input,
    });

    for (const q of group.feedbacks) {
      const flatFeedbacks: UserFeedback[] = q.comments.map(transformComment);
      const turnFeedbacks: QuestionTurnFeedback[] = (q.turns ?? []).map((t) => ({
        turnText: t.turnText,
        feedbacks: t.comments.map(transformComment),
      }));

      if (q.questionType === "Overall") {
        surveyLevel.push(...flatFeedbacks);
      } else {
        questions.push({
          questionNumber: q.questionNumber ?? "",
          questionText: q.questionText ?? "",
          feedbacks: flatFeedbacks,
          turns: turnFeedbacks,
        });
      }
    }

    // Sort questions numerically
    questions.sort((a, b) =>
      a.questionNumber.localeCompare(b.questionNumber, undefined, { numeric: true })
    );

    // Collect unique admin names (question-level + per-turn)
    const adminSet = new Set<string>();
    for (const fb of surveyLevel) adminSet.add(fb.userName);
    for (const q of questions) {
      for (const fb of q.feedbacks) adminSet.add(fb.userName);
      for (const t of q.turns ?? []) {
        for (const fb of t.feedbacks) adminSet.add(fb.userName);
      }
    }

    return {
      surveyLevelFeedback: surveyLevel,
      questionFeedback: questions,
      admins: Array.from(adminSet),
    };
  }, [group]);

  const handleToggleStatus = () => {
    setStatus((prev) => (prev === "New" ? "Reviewed" : "New"));
  };

  const handleAdminFilterChange = (event: SelectChangeEvent<string>) => {
    setAdminFilter(event.target.value);
  };

  // Filter feedbacks based on selected admin and search
  const filterFeedbacks = (feedbacks: UserFeedback[]) => {
    return feedbacks.filter((f) => {
      const matchesAdmin =
        adminFilter === "all" || f.userName === adminFilter;
      const matchesSearch =
        searchQuery === "" ||
        f.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        f.comment.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesAdmin && matchesSearch;
    });
  };

  const totalSurveyComments = filterFeedbacks(surveyLevelFeedback).length;
  const totalQuestionComments = questionFeedback.reduce((acc, q) => {
    const questionLevelCount = filterFeedbacks(q.feedbacks).length;
    const turnsCount = (q.turns ?? []).reduce(
      (sum, t) => sum + filterFeedbacks(t.feedbacks).length,
      0
    );
    return acc + questionLevelCount + turnsCount;
  }, 0);

  return (
    <Box sx={{ maxWidth: "1072px", mx: "auto", px: 5, py: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        {/* Left side */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {/* Back Button */}
          <Button
            startIcon={<ArrowBackIcon sx={{ width: 16, height: 16 }} />}
            onClick={onBack}
            sx={{
              height: "32px",
              borderRadius: derinSpacing.buttonBorderRadius,
              ...derinTypography.button,
              color: derinColors.textPrimary,
              textTransform: "none",
              "&:hover": {
                background: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            Back to List
          </Button>

          {/* Document Icon */}
          <DescriptionOutlinedIcon
            sx={{ width: 24, height: 24, color: derinColors.textSecondary }}
          />

          {/* Survey Info */}
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: "18px",
                  lineHeight: "27px",
                  letterSpacing: "-0.44px",
                  color: derinColors.textPrimary,
                }}
              >
                {group.surveyName}
              </Typography>
              {/* Version Badge */}
              <Box
                sx={{
                  border: `1px solid ${derinColors.borderLight}`,
                  borderRadius: derinSpacing.buttonBorderRadius,
                  px: 1,
                  py: 0.25,
                }}
              >
                <Typography
                  sx={{
                    ...derinTypography.badge,
                    color: derinColors.textPrimary,
                  }}
                >
                  v{group.version}
                </Typography>
              </Box>
            </Box>
            <Typography
              sx={{
                ...derinTypography.subtitle,
                color: derinColors.textSecondary,
              }}
            >
              {group.organizationName} • {formatDate(group.lastSubmittedAt)} •{" "}
              {group.submittedBy.length} Admins
            </Typography>
          </Box>
        </Box>

        {/* Right side - Action Button */}
        <Button
          onClick={handleToggleStatus}
          sx={{
            background: status === "New" ? derinColors.primaryDark : derinColors.backgroundWhite,
            color: status === "New" ? derinColors.textWhite : derinColors.textPrimary,
            border:
              status === "New" ? "none" : `1px solid ${derinColors.borderLight}`,
            borderRadius: derinSpacing.buttonBorderRadius,
            height: derinSpacing.buttonHeight,
            ...derinTypography.button,
            textTransform: "none",
            pl: status === "New" ? 1.5 : 2,
            pr: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
            "&:hover": {
              background: status === "New" ? derinColors.hoverButtonDark : derinColors.hoverButtonLight,
            },
          }}
        >
          {status === "New" && (
            <CheckCircleOutlineOutlinedIcon sx={{ fontSize: 16 }} />
          )}
          {status === "New" ? "Mark as Reviewed" : "Mark as New"}
        </Button>
      </Box>

      {/* Summary Section */}
      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: "20px",
            lineHeight: "36px",
            letterSpacing: "0.07px",
            color: derinColors.textPrimary,
            mb: 0.5,
          }}
        >
          Summary of Feedback
        </Typography>
        <Typography
          sx={{
            ...derinTypography.body,
            letterSpacing: "-0.31px",
            color: derinColors.textSecondary,
            mb: 2,
          }}
        >
          Summary of feedback from all administrators. You can access individual
          feedback using the name filter.
        </Typography>

        {/* Filters */}
        <Box sx={{ display: "flex", gap: 1 }}>
          {/* Admin Dropdown */}
          <Select
            value={adminFilter}
            onChange={handleAdminFilterChange}
            IconComponent={KeyboardArrowDownIcon}
            sx={derinInputStyles.select}
          >
            <MenuItem value="all">All admins</MenuItem>
            {admins.map((admin) => (
              <MenuItem key={admin} value={admin}>
                {admin}
              </MenuItem>
            ))}
          </Select>

          {/* Search Input */}
          <Box sx={derinInputStyles.searchContainer}>
            <SearchIcon sx={{ color: derinColors.textSecondary, fontSize: 16, mr: 1 }} />
            <InputBase
              placeholder="Search by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={derinInputStyles.searchInput}
            />
          </Box>
        </Box>
      </Box>

      {/* Cards Container */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: derinSpacing.sectionGap }}>
        {/* Survey-Level Feedback Card - only show if there are survey-level feedbacks */}
        {surveyLevelFeedback.length > 0 && (
          <Box sx={derinCardStyles.container}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: "16px",
              }}
            >
              <SectionHeader
                title="Survey-Level Feedback"
                commentsCount={totalSurveyComments}
              />

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "flex-start",
                  gap: derinSpacing.feedbackGapSurvey,
                }}
              >
                {filterFeedbacks(surveyLevelFeedback).map((fb) => (
                  <Box
                    key={fb.id}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      gap: derinSpacing.headerGap,
                      flex: 1,
                      borderRadius: derinSpacing.containerBorderRadius,
                    }}
                  >
                    <Typography
                      sx={{
                        ...derinTypography.body,
                        color: derinColors.textPrimary,
                      }}
                    >
                      {fb.userName} at {fb.timestamp}
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        padding: "13px 12px 0px 12px",
                        minHeight: "125px",
                        background: getColorForUser(fb.userName).bg,
                        borderRadius: derinSpacing.buttonBorderRadius,
                        boxSizing: "border-box",
                        alignSelf: "stretch",
                      }}
                    >
                      <Typography
                        sx={{
                          ...derinTypography.bodyBold,
                          color: derinColors.textBlack,
                        }}
                      >
                        {fb.comment}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}

        {/* Question Feedback Card */}
        {totalQuestionComments > 0 && (
          <Box sx={derinCardStyles.container}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: "19px",
              }}
            >
              <SectionHeader
                title="Question Feedback"
                commentsCount={totalQuestionComments}
              />

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: derinSpacing.questionsListGap,
                  width: "100%",
                }}
              >
                {questionFeedback.map((question) => {
                  const filteredQuestionLevel = filterFeedbacks(question.feedbacks);
                  const filteredTurns = (question.turns ?? [])
                    .map((t) => ({
                      turnText: t.turnText,
                      feedbacks: filterFeedbacks(t.feedbacks),
                    }))
                    .filter((t) => t.feedbacks.length > 0);

                  if (
                    filteredQuestionLevel.length === 0 &&
                    filteredTurns.length === 0
                  )
                    return null;

                  const renderFeedbacksRow = (feedbacks: UserFeedback[]) => (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        flexWrap: "wrap",
                        alignItems: "flex-start",
                        gap: derinSpacing.feedbackGapQuestion,
                      }}
                    >
                      {feedbacks.map((fb) => (
                        <Box
                          key={fb.id}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            gap: derinSpacing.feedbackGapQuestion,
                            width: "418px",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                              gap: derinSpacing.headerGap,
                              height: "32px",
                            }}
                          >
                            <VerticalBar />
                            <Typography
                              sx={{
                                ...derinTypography.body,
                                color: derinColors.textPrimary,
                              }}
                            >
                              {fb.userName} at {fb.timestamp}
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                              padding: "12px 12px 0px 14px",
                              width: "418px",
                              height: "120px",
                              background: getColorForUser(fb.userName).bgLight,
                              borderLeft: `2px solid ${derinColors.borderLeftAccent}`,
                              borderRadius: derinSpacing.buttonBorderRadius,
                              boxSizing: "border-box",
                            }}
                          >
                            <Typography
                              sx={{
                                ...derinTypography.body,
                                color: derinColors.textComment,
                              }}
                            >
                              {fb.comment}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  );

                  return (
                    <Box
                      key={question.questionNumber}
                      sx={derinCardStyles.questionContainer}
                    >
                      {/* Question Header */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "flex-start",
                          gap: derinSpacing.feedbackGapQuestion,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            padding: "2px 8px",
                            height: "22px",
                            background: derinColors.badgeBackground,
                            borderRadius: derinSpacing.buttonBorderRadius,
                            flexShrink: 0,
                          }}
                        >
                          <Typography
                            sx={{
                              ...derinTypography.badge,
                              color: derinColors.primaryDark,
                            }}
                          >
                            {question.questionNumber}
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            ...derinTypography.bodySemiBold,
                            color: derinColors.textPrimary,
                          }}
                        >
                          {question.questionText}
                        </Typography>
                      </Box>

                      {/* Question-level feedbacks (no turn association) */}
                      {filteredQuestionLevel.length > 0 &&
                        renderFeedbacksRow(filteredQuestionLevel)}

                      {/* Per-turn feedbacks (conversational only) */}
                      {filteredTurns.map((turn) => (
                        <Box
                          key={turn.turnText}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            gap: derinSpacing.feedbackGapQuestion,
                            width: "100%",
                            paddingLeft: "24px",
                          }}
                        >
                          <Typography
                            sx={{
                              ...derinTypography.body,
                              fontStyle: "italic",
                              color: derinColors.textSecondary,
                            }}
                          >
                            {turn.turnText}
                          </Typography>
                          {renderFeedbacksRow(turn.feedbacks)}
                        </Box>
                      ))}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default FeedbackDetailView;
